import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Divider,
  Badge,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Cloud as CloudIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import ClaimsWorkflowService from '../../services/claimsWorkflowService';

const StatusCard = styled(Card)(({ theme, status }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
  ...(status === 'submitted' && {
    borderLeft: `4px solid ${theme.palette.info.main}`,
  }),
  ...(status === 'approved' && {
    borderLeft: `4px solid ${theme.palette.success.main}`,
  }),
  ...(status === 'denied' && {
    borderLeft: `4px solid ${theme.palette.error.main}`,
  }),
  ...(status === 'pending' && {
    borderLeft: `4px solid ${theme.palette.warning.main}`,
  }),
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ClaimsManagement = ({ bills }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Calculate 6 months ago for default date range
  const sixMonthsAgo = new Date();
  sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
  sixMonthsAgo.setHours(0, 0, 0, 0);
  
  const [filters, setFilters] = useState({
    status: 'all',
    dateRange: { start: sixMonthsAgo, end: new Date() },
    clearinghouse: 'all'
  });
  const [workflowStats, setWorkflowStats] = useState({
    total: 0,
    pending: 0,
    submitted: 0,
    approved: 0,
    denied: 0,
    processing: 0
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Mock claims data - in real implementation, this would come from the claims service
  useEffect(() => {
    // Generate mock claims from bills
    const mockClaims = bills.map(bill => ({
      id: `claim_${bill.id}`,
      claimNumber: `CLM${bill.billNumber || bill.id}`,
      billId: bill.id,
      patientName: bill.patientName,
      patientId: bill.patientId,
      totalAmount: bill.totalAmount,
      status: getRandomClaimStatus(),
      submissionDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
      clearinghouse: Math.random() > 0.5 ? 'Office Ally' : 'Optum',
      responseDate: Math.random() > 0.7 ? new Date() : null,
      denialReason: Math.random() > 0.8 ? 'Missing documentation' : null,
      resubmissionCount: Math.floor(Math.random() * 3)
    }));
    
    setClaims(mockClaims);
    calculateWorkflowStats(mockClaims);
  }, [bills]);

  const getRandomClaimStatus = () => {
    const statuses = ['pending', 'submitted', 'approved', 'denied', 'processing'];
    return statuses[Math.floor(Math.random() * statuses.length)];
  };

  const calculateWorkflowStats = (claimsData) => {
    const stats = claimsData.reduce((acc, claim) => {
      acc.total++;
      acc[claim.status] = (acc[claim.status] || 0) + 1;
      return acc;
    }, {
      total: 0,
      pending: 0,
      submitted: 0,
      approved: 0,
      denied: 0,
      processing: 0
    });
    setWorkflowStats(stats);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'denied':
        return 'error';
      case 'submitted':
        return 'info';
      case 'processing':
        return 'warning';
      case 'pending':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <CheckCircleIcon fontSize="small" />;
      case 'denied':
        return <ErrorIcon fontSize="small" />;
      case 'submitted':
        return <SendIcon fontSize="small" />;
      case 'processing':
        return <ScheduleIcon fontSize="small" />;
      case 'pending':
        return <AssignmentIcon fontSize="small" />;
      default:
        return <AssignmentIcon fontSize="small" />;
    }
  };

  const handleProcessClaim = async (claimId) => {
    try {
      setLoading(true);
      // In real implementation, this would call the claims workflow service
      console.log(`Processing claim: ${claimId}`);
      
      // Simulate processing delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update claim status and recalculate stats
      setClaims(prev => {
        const updated = prev.map(claim => 
          claim.id === claimId 
            ? { ...claim, status: 'submitted', submissionDate: new Date() }
            : claim
        );
        calculateWorkflowStats(updated);
        return updated;
      });
      
    } catch (error) {
      console.error('Failed to process claim:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResubmitClaim = async (claimId) => {
    try {
      setLoading(true);
      console.log(`Resubmitting claim: ${claimId}`);
      
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update claim status and recalculate stats
      setClaims(prev => {
        const updated = prev.map(claim => 
          claim.id === claimId 
            ? { 
                ...claim, 
                status: 'submitted', 
                submissionDate: new Date(),
                resubmissionCount: (claim.resubmissionCount || 0) + 1
              }
            : claim
        );
        calculateWorkflowStats(updated);
        return updated;
      });
      
    } catch (error) {
      console.error('Failed to resubmit claim:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (claim) => {
    // Navigate to the claim details page
    navigate(`/billing/claims/${claim.id}`);
  };

  const handleSubmitNewClaim = () => {
    // Navigate to create new bill page (claims are generated from bills)
    navigate('/billing/create');
  };

  const filteredClaims = claims.filter(claim => {
    if (filters.status !== 'all' && claim.status !== filters.status) return false;
    if (filters.clearinghouse !== 'all' && claim.clearinghouse !== filters.clearinghouse) return false;
    if (filters.dateRange.start && claim.submissionDate < filters.dateRange.start) return false;
    if (filters.dateRange.end && claim.submissionDate > filters.dateRange.end) return false;
    return true;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {/* Workflow Statistics - Compact */}
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>
        <StatusCard sx={{ minWidth: 100, flex: '1 1 auto' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <AssignmentIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              {workflowStats.total}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Total
            </Typography>
          </CardContent>
        </StatusCard>
        
        <StatusCard status="pending" sx={{ minWidth: 100, flex: '1 1 auto' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <AssignmentIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {workflowStats.pending}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Pending
            </Typography>
          </CardContent>
        </StatusCard>
        
        <StatusCard status="submitted" sx={{ minWidth: 100, flex: '1 1 auto' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <SendIcon sx={{ fontSize: 18, color: 'info.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'info.main' }}>
              {workflowStats.submitted}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Submitted
            </Typography>
          </CardContent>
        </StatusCard>
        
        <StatusCard status="processing" sx={{ minWidth: 100, flex: '1 1 auto' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <ScheduleIcon sx={{ fontSize: 18, color: 'warning.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'warning.main' }}>
              {workflowStats.processing}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Processing
            </Typography>
          </CardContent>
        </StatusCard>
        
        <StatusCard status="approved" sx={{ minWidth: 100, flex: '1 1 auto' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'success.main' }}>
              {workflowStats.approved}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Approved
            </Typography>
          </CardContent>
        </StatusCard>
        
        <StatusCard status="denied" sx={{ minWidth: 100, flex: '1 1 auto' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <ErrorIcon sx={{ fontSize: 18, color: 'error.main' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'error.main' }}>
              {workflowStats.denied}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Denied
            </Typography>
          </CardContent>
        </StatusCard>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="submitted">Submitted</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="approved">Approved</MenuItem>
                  <MenuItem value="denied">Denied</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Clearinghouse</InputLabel>
                <Select
                  value={filters.clearinghouse}
                  onChange={(e) => setFilters(prev => ({ ...prev, clearinghouse: e.target.value }))}
                  label="Clearinghouse"
                >
                  <MenuItem value="all">All Clearinghouses</MenuItem>
                  <MenuItem value="Office Ally">Office Ally</MenuItem>
                  <MenuItem value="Optum">Optum</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={2.5}>
              <DatePicker
                label="From Date"
                value={filters.dateRange.start}
                onChange={(date) => {
                  // Limit to max 6 months ago
                  const minDate = new Date();
                  minDate.setMonth(minDate.getMonth() - 6);
                  if (date && date >= minDate) {
                    setFilters(prev => ({ 
                      ...prev, 
                      dateRange: { ...prev.dateRange, start: date } 
                    }));
                    setPage(0);
                  }
                }}
                minDate={(() => { const d = new Date(); d.setMonth(d.getMonth() - 6); return d; })()}
                maxDate={filters.dateRange.end || new Date()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            
            <Grid item xs={12} md={2.5}>
              <DatePicker
                label="To Date"
                value={filters.dateRange.end}
                onChange={(date) => {
                  if (date) {
                    setFilters(prev => ({ 
                      ...prev, 
                      dateRange: { ...prev.dateRange, end: date } 
                    }));
                    setPage(0);
                  }
                }}
                minDate={filters.dateRange.start}
                maxDate={new Date()}
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            
            <Grid item xs={12} md={1}>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: '40px' }}>
                (6 mo max)
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Claims Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Claims ({filteredClaims.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                color="primary"
                onClick={handleSubmitNewClaim}
              >
                Submit New Claim
              </Button>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
              >
                Refresh
              </Button>
            </Box>
          </Box>
          
          <TableContainer component={Paper} variant="outlined">
            <StyledTable>
              <TableHead>
                <TableRow>
                  <TableCell>Claim Number</TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Clearinghouse</TableCell>
                  <TableCell>Submission Date</TableCell>
                  <TableCell>Response Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClaims.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((claim) => (
                  <TableRow key={claim.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                        {claim.claimNumber}
                      </Typography>
                      {claim.resubmissionCount > 0 && (
                        <Chip 
                          label={`Resubmitted ${claim.resubmissionCount}x`} 
                          size="small" 
                          color="warning" 
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {claim.patientName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          MRN: {claim.patientMRN || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        ${parseFloat(claim.totalAmount || 0).toLocaleString('en-US', { 
                          minimumFractionDigits: 2, 
                          maximumFractionDigits: 2 
                        })}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Chip 
                        icon={getStatusIcon(claim.status)}
                        label={claim.status} 
                        color={getStatusColor(claim.status)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                      {claim.denialReason && (
                        <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                          {claim.denialReason}
                        </Typography>
                      )}
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CloudIcon fontSize="small" color="primary" />
                        <Typography variant="body2">
                          {claim.clearinghouse}
                        </Typography>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {claim.submissionDate ? claim.submissionDate.toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {claim.responseDate ? claim.responseDate.toLocaleDateString() : 'N/A'}
                      </Typography>
                    </TableCell>
                    
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewDetails(claim)}
                            sx={{ 
                              backgroundColor: 'primary.light',
                              '&:hover': { backgroundColor: 'primary.main', color: 'white' }
                            }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        
                        {claim.status === 'pending' && (
                          <Tooltip title="Process Claim">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => handleProcessClaim(claim.id)}
                              disabled={loading}
                              sx={{ 
                                backgroundColor: 'success.light',
                                '&:hover': { backgroundColor: 'success.main', color: 'white' }
                              }}
                            >
                              {loading ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {claim.status === 'denied' && (
                          <Tooltip title="Resubmit Claim">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => handleResubmitClaim(claim.id)}
                              disabled={loading}
                              sx={{ 
                                backgroundColor: 'warning.light',
                                '&:hover': { backgroundColor: 'warning.main', color: 'white' }
                              }}
                            >
                              {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </StyledTable>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={filteredClaims.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
          
          {filteredClaims.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">
                No claims found matching the current filters.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ClaimsManagement;
